import { CloseIcon } from '@chakra-ui/icons'
import { Box, Button, Flex, IconButton, Text, Tooltip } from '@chakra-ui/react'
import { ActionConfirm } from '@components/Alerts/ActionConfirm'
import { ActionButton, DeleteButton, SolidActionButton } from '@components/Buttons'
import { useFormWidth } from '@components/forms/FinalForm/context'
import {
  AnyObject,
  BaseAuthentication,
  Encounter,
  EncounterType,
  SubmittedAuthentication,
} from '@hb/shared'
import { FloppyDiskIcon } from '@icons/FloppyDiskIcon'
import { Dispatch, SetStateAction, useState } from 'react'
import { useFormState } from 'react-final-form'
import { useEncounterForm } from '../EncounterFormProvider'
import { AuthenticateEncounterModal } from './AuthenticateEncounterModal'
import { EncounterDateInput } from './EncounterDateInput'

interface EncounterFormHeaderActionRowProps {
  typeName: string
  examId: string | null
  examType: EncounterType
  encounter: Encounter | null
  practiceId: string | null
  confirmingCancel: boolean
  setConfirmingCancel: Dispatch<SetStateAction<boolean>>
  pendingHistoricalAuth: BaseAuthentication<AnyObject> | 'current' | null
  setPendingHistoricalAuth: Dispatch<
    SetStateAction<BaseAuthentication<AnyObject> | 'current' | null>
  >
  setViewingHistoricalAuth: Dispatch<
    SetStateAction<BaseAuthentication<AnyObject> | 'current' | null>
  >
  authenticateModalOpen: boolean
  setAuthenticateModalOpen: Dispatch<SetStateAction<boolean>>
  authChecked: boolean
  setAuthChecked: Dispatch<SetStateAction<boolean>>
  coSignRequired: boolean
  cannotAuthenticateMessage: string | null
  submitting: boolean
  canDeleteDraft: boolean
  onCancel: () => void
  onDiscard: () => void
  onSubmit: () => void
  onAuthenticate: (auth: SubmittedAuthentication) => Promise<void>
  onDeleteDraft: () => void
  // When provided, clicking Authenticate defers to the body (e.g. to show the Time & Work Log panel first).
  onRequestAuthenticate?: () => void
}

// Row 2: Cancel confirmation OR exam date + action buttons.
// On mobile the date input and action buttons stack vertically — side by side they overlap.
export const EncounterFormHeaderActionRow = ({
  typeName,
  examId,
  examType,
  encounter,
  practiceId,
  confirmingCancel,
  setConfirmingCancel,
  pendingHistoricalAuth,
  setPendingHistoricalAuth,
  setViewingHistoricalAuth,
  authenticateModalOpen,
  setAuthenticateModalOpen,
  authChecked,
  setAuthChecked,
  coSignRequired,
  cannotAuthenticateMessage,
  submitting,
  canDeleteDraft,
  onCancel,
  onDiscard,
  onSubmit,
  onAuthenticate,
  onDeleteDraft,
  onRequestAuthenticate,
}: EncounterFormHeaderActionRowProps) => {
  const { mobileLayout } = useFormWidth()
  const { dirty, submitError } = useFormState({
    subscription: { dirty: true, submitError: true },
  })
  // Mobile: the pre-visit contact link moves from the date row into the action-button row.
  const { includePreVisitContact, setIncludePreVisitContact } = useEncounterForm()
  const showMobilePreVisitButton =
    mobileLayout && examType === EncounterType.ANTEPARTUM && !includePreVisitContact

  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false)
  const [hoveringDiscard, setHoveringDiscard] = useState(false)

  return (
    <Flex
      bg="gray.50"
      _dark={{
        bg: 'gray.800',
      }}
      flexFlow={mobileLayout ? 'column' : 'row'}
      justify="flex-end"
      align={mobileLayout ? 'stretch' : 'center'}
      py={1}
      gap={mobileLayout ? 0.5 : 2}
      px={3}
      w="100%">
      {confirmingCancel ? (
        <>
          <Text
            flex={1}
            minW={0}
            fontSize="sm"
            fontFamily="Hero-New"
            color="red.600"
            fontWeight={500}>
            You have unsaved changes. Go back anyway?
          </Text>
          <Flex align="center" gap={2} flexWrap="wrap" justify="flex-end">
            <ActionButton
              colorScheme="gray"
              size="sm"
              borderRadius="full"
              onClick={() => setConfirmingCancel(false)}>
              No, stay here
            </ActionButton>
            <Button
              colorScheme="red"
              variant="outline"
              bg="red.50"
              _dark={{
                bg: 'red.900',
              }}
              size="sm"
              borderRadius="full"
              onClick={onCancel}>
              Yes, go back
            </Button>
          </Flex>
        </>
      ) : pendingHistoricalAuth ? (
        <>
          <Text
            flex={1}
            minW={0}
            fontSize="sm"
            fontFamily="Hero-New"
            color="orange.600"
            fontWeight={500}>
            You have unsaved changes. View previous version anyway?
          </Text>
          <Flex align="center" gap={2} flexWrap="wrap" justify="flex-end">
            <ActionButton
              colorScheme="gray"
              size="sm"
              borderRadius="full"
              onClick={() => setPendingHistoricalAuth(null)}>
              No, keep editing
            </ActionButton>
            <Button
              colorScheme="orange"
              variant="outline"
              bg="orange.50"
              _dark={{ bg: 'orange.900' }}
              size="sm"
              borderRadius="full"
              onClick={() => {
                setViewingHistoricalAuth(pendingHistoricalAuth)
                setPendingHistoricalAuth(null)
              }}>
              Yes, view previous version
            </Button>
          </Flex>
        </>
      ) : (
        <>
          <AuthenticateEncounterModal
            isOpen={authenticateModalOpen}
            onClose={() => setAuthenticateModalOpen(false)}
            typeName={typeName}
            encounter={encounter}
            examId={examId}
            examType={examType}
            practiceId={practiceId}
            coSignRequired={coSignRequired}
            authChecked={authChecked}
            setAuthChecked={setAuthChecked}
            onAuthenticate={onAuthenticate}
          />
          <Flex align="center" gap={2} flex={1} minW={0}>
            <EncounterDateInput />
            {/* Mobile: close is a compact icon button on the date row */}
            {mobileLayout ? (
              <IconButton
                aria-label="Close"
                size="xs"
                variant="outline"
                colorScheme="gray"
                borderRadius="full"
                bg="white"
                _dark={{ bg: 'gray.900' }}
                flexShrink={0}
                icon={<CloseIcon boxSize={2.5} />}
                onClick={onCancel}
              />
            ) : null}
          </Flex>
          <Flex align="center" gap={2} flexWrap="wrap" justify="flex-end">
            {showMobilePreVisitButton ? (
              <>
                <Button
                  variant="link"
                  fontSize="13px"
                  colorScheme="gray"
                  fontWeight={400}
                  flexShrink={0}
                  onClick={() => setIncludePreVisitContact(true)}>
                  + Add pre-visit contact
                </Button>
                <Box flex={1} />
              </>
            ) : null}
            {submitError ? (
              <Text px={3} bg="whiteAlpha.800" color="red.600" borderRadius={4} fontWeight={500}>
                {submitError}
              </Text>
            ) : null}
            {dirty ? (
              <>
                <Button
                  size={mobileLayout ? 'xs' : 'sm'}
                  colorScheme={hoveringDiscard ? 'red' : 'gray'}
                  variant="link"
                  onMouseEnter={() => setHoveringDiscard(true)}
                  onMouseLeave={() => setHoveringDiscard(false)}
                  onClick={() => setConfirmDiscardOpen(true)}>
                  {mobileLayout
                    ? 'UNSAVED'
                    : hoveringDiscard
                      ? 'DISCARD CHANGES'
                      : 'UNSAVED CHANGES'}
                </Button>
                <ActionConfirm
                  isOpen={confirmDiscardOpen}
                  onClose={() => setConfirmDiscardOpen(false)}
                  colorScheme="red"
                  header="Discard changes"
                  body="Are you sure you want to discard your unsaved changes?"
                  confirmText="Discard"
                  onConfirm={() => {
                    onDiscard()
                    setConfirmDiscardOpen(false)
                  }}
                />
              </>
            ) : null}
            {!mobileLayout ? (
              <ActionButton colorScheme="gray" size="xs" borderRadius="full" onClick={onCancel}>
                CLOSE
              </ActionButton>
            ) : null}
            {mobileLayout ? (
              <Tooltip hasArrow placement="top" label="Save Draft">
                <IconButton
                  aria-label="Save Draft"
                  size="xs"
                  variant="outline"
                  colorScheme="green"
                  borderRadius="full"
                  bg="white"
                  _dark={{ bg: 'gray.900' }}
                  isLoading={submitting}
                  icon={<FloppyDiskIcon w={4} h={4} />}
                  onClick={onSubmit}
                />
              </Tooltip>
            ) : (
              <ActionButton
                size="xs"
                borderRadius="full"
                px={3}
                isLoading={submitting}
                onClick={onSubmit}>
                SAVE DRAFT
              </ActionButton>
            )}
            <Tooltip
              hasArrow
              placement="right"
              bg={cannotAuthenticateMessage ? 'gray.500' : 'blue.500'}
              label={cannotAuthenticateMessage ?? `Authenticate ${typeName}`}>
              <SolidActionButton
                aria-label={`Authenticate ${typeName}`}
                size="xs"
                borderRadius="full"
                // Dark forest green — the primary action, always visually dominant (spec 0.6.3).
                bg="#1f4d2e"
                color="white"
                _hover={{ bg: '#163d23' }}
                _active={{ bg: '#163d23' }}
                isDisabled={!!cannotAuthenticateMessage}
                onClick={() => {
                  if (onRequestAuthenticate) {
                    onRequestAuthenticate()
                  } else {
                    setAuthChecked(false)
                    setAuthenticateModalOpen(true)
                  }
                }}>
                {mobileLayout ? 'AUTH' : 'AUTHENTICATE'}
              </SolidActionButton>
            </Tooltip>
            {canDeleteDraft ? (
              <DeleteButton
                size={mobileLayout ? 'xs' : 'sm'}
                itemName="draft"
                actionName="Delete"
                borderRadius="full"
                alertBody="Are you sure you want to delete this draft encounter? This action cannot be undone."
                onDelete={onDeleteDraft}
              />
            ) : null}
          </Flex>
        </>
      )}
    </Flex>
  )
}
